import { VaniaGlassPanel } from '@vanialabs/ui-engine';
import { formatPersianNumber } from '../../../app/components/foundation';
import type { MonitoringSnapshot, MonitoringServiceStatus } from '../../monitoring/types/monitoring.types';

interface DashboardHealthCenterProps {
  services: string[];
  monitoring: MonitoringSnapshot | null;
  loading: boolean;
  errorMessage: string | null;
}

const SERVICE_KEYS: Record<string, keyof MonitoringSnapshot['services'] | undefined> = {
  API: 'api',
  Database: 'database',
  Redis: 'redis',
};

function statusLabel(service: MonitoringServiceStatus | undefined, loading: boolean, hasError: boolean) {
  if (loading) return '—';
  if (hasError) return 'خطا در دریافت';
  if (!service) return 'در دسترس نیست';
  if (service.status === 'ok' || service.status === 'healthy') return 'پایدار';
  if (service.status === 'error') return 'خطا';
  return service.status;
}

function statusClass(service: MonitoringServiceStatus | undefined, loading: boolean, hasError: boolean) {
  if (loading) return 'is-loading';
  if (hasError || service?.status === 'error') return 'is-error';
  if (!service || service.status === 'unavailable' || service.status === 'unknown') return 'is-unavailable';
  return 'is-ok';
}

function formatUptime(seconds: number) {
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return days > 0 ? `${formatPersianNumber(days)} روز و ${formatPersianNumber(hours)} ساعت` : `${formatPersianNumber(hours)} ساعت و ${formatPersianNumber(minutes)} دقیقه`;
}

function formatCheckedAt(seconds: number) {
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime()) ? 'در دسترس نیست' : date.toLocaleString('fa-IR');
}

export function DashboardHealthCenter({ services, monitoring, loading, errorMessage }: DashboardHealthCenterProps) {
  const hasError = Boolean(errorMessage);
  const stateMessage = loading
    ? '—'
    : errorMessage || (monitoring ? 'آخرین وضعیت دریافت‌شده از مانیتورینگ' : 'داده سلامت در دسترس نیست');

  return <div className="vania-command-health-center" aria-live="polite">
    <div className="vania-command-health-state" title={stateMessage}>
      <i className={`vania-command-health-state-dot ${loading ? 'is-loading' : hasError ? 'is-error' : monitoring ? 'is-ok' : 'is-unavailable'}`} aria-hidden="true" />
      <span>{stateMessage}</span>
    </div>
    {monitoring ? <div className="vania-command-health-summary">
      <span>آپتایم: {formatUptime(monitoring.uptime_seconds)}</span>
      <span>آخرین بررسی: {formatCheckedAt(monitoring.checked_at)}</span>
      <span>نشست فعال: {formatPersianNumber(monitoring.active_sessions)}</span>
    </div> : null}
    <div className="vania-command-health-list">
      {services.map((service) => {
        const serviceStatus = SERVICE_KEYS[service] ? monitoring?.services[SERVICE_KEYS[service]!] : undefined;
        const label = statusLabel(serviceStatus, loading, hasError);
        const detail = serviceStatus?.message || (service === 'Bot' ? 'داده سلامت Bot در API موجود نیست' : stateMessage);
        return <VaniaGlassPanel key={service} className="vania-command-health-item" tabIndex={0} aria-label={`${service}: ${label}`} title={detail}>
          <span className="vania-command-health-label">{service}</span>
          <span className="vania-command-health-badge"><i className={statusClass(serviceStatus, loading, hasError)} aria-hidden="true" />{label}</span>
        </VaniaGlassPanel>;
      })}
    </div>
  </div>;
}
